import { Box, Button, CircularProgress } from "@mui/material";
import { UUID } from "crypto";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AutoAwesome, FileDownloadOutlined, Slideshow, SpatialAudioOff } from "@mui/icons-material";
import { useDeepChatSocketContext } from "../../../contexts/DeepChatSocketContext";
import emitSnackbar from "../../../emitSnackbar";
import Loader from "../../Loader";
import { BodyPanel, MainPanel, RightPanel } from "../Layout/Panels";
import StudyHeader from "../Layout/StudyHeader";
import useMarketResearchStore from "../useMarketResearchStore";
import useStudyMetadataStore from "../useStudyMetadataStore";
import QuestionInputBox from "./QuestionInputBox";
import StudyQuestionsGrid from "./StudyQuestionsGrid";
import StudyRightPanelBase from "./StudyRightPanelBase";
import ChangeStudyButton from "../ChangeStudyButton";

interface UriParams {
  studyId: UUID;
}

export default function ViewStudy() {
  const { studyId } = useParams() as unknown as UriParams;
  const navigate = useNavigate();
  const DeepChatSocket = useDeepChatSocketContext();
  const [isPPTDownloading, setIsPPTDownloading] = useState(false);

  const {
    studyQuestions,
    isLoadingQuestions,
    setQuestion,
    setSelectedStudyIds,
    question,
    getStudy,
    askQuestion,
    toggleLoadingChat,
    clearChatSession,
    toggleFilterDrawer,
    setScope,
    toggleStudySummary,
    resetRespondentsArray,
    filterCount,
    downloadFile,
  } = useMarketResearchStore();

  //set respondentIdsArray to default array on render
  useEffect(() => {
    toggleFilterDrawer(false);
    resetRespondentsArray();
    setScope("chat");

    // TODO: This is a bad practice and we need to work to stop doing it
    setSelectedStudyIds([studyId]);
  }, []);

  // This is not ideal but only the "List Study" model contains the `study_format`
  const pmrStudy = studyId ? getStudy(studyId) : undefined;
  const study_format = pmrStudy?.study_format ?? "default";

  const { study, isLoading } = useStudyMetadataStore(studyId as UUID);

  const askDeepchatQuestion = (question: string, question_id?: string, numOfResponses?: number) => {
    if (pmrStudy?.study_title) {
      clearChatSession();
      toggleFilterDrawer(false);
      askQuestion(DeepChatSocket.sendJsonMessage, question, [pmrStudy.study_guid], question_id, numOfResponses);
      navigate(`/market-research/threads/`);
    }
  };

  const navigateToSummary = () => {
    navigate(`/market-research/study/${study?.study_guid}/summary`);
  };

  const downloadPPT = async () => {
    setIsPPTDownloading(true);
    const isDownloadSuccessful = await downloadFile(
      study?.files[0]?.uploaded_file?.uploaded_file_guid ?? "",
      study?.files[0]?.uploaded_file?.s3_file_location ?? "",
    );

    setIsPPTDownloading(false);
    if (isDownloadSuccessful) {
      emitSnackbar("PPT file downloaded successfully", "success", { autoHideDuration: 2000 });
    } else {
      emitSnackbar("Error downloading PPT file", "error", { autoHideDuration: 2000 });
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <MainPanel>
      <BodyPanel>
        <Box sx={{ padding: "24px", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 0 }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
                flexDirection={"row"}
              >
                <StudyHeader study={study} />
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {study_format == "default" && (
                    <Link
                      to={`/market-research/study/${studyId}/summary`}
                      style={{ textDecoration: "none", display: "none" }}
                    >
                      <Button variant="outlined" color="primary" sx={{ color: "#003087" }} onClick={toggleStudySummary}>
                        STUDY SUMMARY
                      </Button>
                    </Link>
                  )}
                  <ChangeStudyButton />
                </Box>
              </Box>
            </Box>
            <Box sx={{ my: "2em" }}>
              <QuestionInputBox
                isDisabled={false}
                onQuestionInput={(text: string) => {
                  setQuestion(text);
                }}
                filterCount={filterCount()}
                onFilterClick={() => toggleFilterDrawer()}
                onSubmit={() => askDeepchatQuestion(question)}
                question={question}
                hideFilter={study_format.toLowerCase() === "ppt"}
                placeholder="Ask Deepsense..."
              />
            </Box>
          </Box>

          {study_format.toLowerCase() === "transcript" && (
            <StudyQuestionsGrid
              studyQuestions={studyQuestions}
              isLoadingQuestions={isLoadingQuestions}
              askDeepchatQuestion={askDeepchatQuestion}
            />
          )}
        </Box>
      </BodyPanel>

      <RightPanel sx={{ display: "flex", flexDirection: "column" }}>
        {pmrStudy &&
          (() => {
            switch (study_format.toLowerCase()) {
              case "ppt":
                return (
                  <StudyRightPanelBase
                    study={pmrStudy}
                    showNoOfRespondents={false}
                    menuItems={[
                      {
                        label: "Study Summary",
                        icon: <AutoAwesome />,
                        onClick: () => navigateToSummary(),
                      },
                      {
                        label: "Presentation",
                        icon: <Slideshow />,
                        rightIcon: isPPTDownloading ? <CircularProgress size={20} /> : <FileDownloadOutlined />,
                        onClick: () => {
                          downloadPPT();
                        },
                      },
                    ]}
                  />
                );

              case "transcript":
                return (
                  <StudyRightPanelBase
                    study={pmrStudy}
                    showNoOfRespondents={true}
                    menuItems={[
                      {
                        label: "Study Summary",
                        icon: <AutoAwesome />,
                        onClick: () => navigateToSummary(),
                      },
                      {
                        label: "Interviews",
                        icon: <SpatialAudioOff />,
                        onClick: () => navigate(`/market-research/study/${studyId}/respondents`),
                      },
                    ]}
                  />
                );
            }
          })()}
      </RightPanel>
    </MainPanel>
  );
}
